import { useState } from "react";
import { isModuleEntitlement, type Workspace } from "@nahal/api-client";
import { WorkspaceModulesSheet } from "./WorkspaceModulesSheet";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  KeyValue,
  LoadingBlock,
  PageHeader,
  StatusBadge,
  toast,
} from "@nahal/ui";
import { api } from "../../lib/api";
import { useProducts, useSubscriptions, useWorkspaces, useModuleFeature } from "../../lib/queries";

export function ProductDetailScreen() {
  const { productCode } = useParams({ from: "/products/$productCode" });
  const navigate = useNavigate();
  const products = useProducts();
  const subscriptions = useSubscriptions();
  const workspaces = useWorkspaces();
  const moduleAccessEnabled = useModuleFeature();
  const [moduleWorkspace, setModuleWorkspace] = useState<Workspace | null>(null);

  if (products.isLoading) return <LoadingBlock />;

  const product = products.data?.find((item) => item.code === productCode);
  if (!product) return <EmptyState title="Product not found" />;

  const subscription = subscriptions.data?.find((item) => item.productCode === productCode);
  const owned = workspaces.data?.filter((item) => item.productCode === productCode) ?? [];

  async function open(workspaceId: string) {
    try {
      const { token, appUrl } = await api.workspaceToken(workspaceId);
      if (!appUrl) {
        toast.error("That product has no app address configured yet.");
        return;
      }
      window.location.href = `${appUrl}/sso?token=${encodeURIComponent(token)}`;
    } catch {
      toast.error("Could not open that workspace.");
    }
  }

  return (
    <>
      <PageHeader
        title={product.name}
        description={product.tagline ?? undefined}
        meta={
          product.owned ? (
            <StatusBadge status={subscription?.status ?? "ACTIVE"} />
          ) : (
            <StatusBadge status="Not subscribed" tone="neutral" />
          )
        }
        actions={
          <Button
            variant="ghost"
            icon={<ArrowLeft className="size-4" />}
            onClick={() => navigate({ to: "/products" })}
          >
            Products
          </Button>
        }
      />

      <div className="space-y-4">
        <Card>
          <CardHeader title="Subscription" />
          <CardBody>
            {subscription ? (
              <KeyValue
                items={[
                  { label: "Plan", value: subscription.planName ?? "not set" },
                  { label: "Status", value: <StatusBadge status={subscription.status} /> },
                  {
                    label: "Since",
                    value: new Date(subscription.startedAt).toLocaleDateString(),
                  },
                  {
                    label: "Ends",
                    value: subscription.endsAt
                      ? new Date(subscription.endsAt).toLocaleDateString()
                      : "open-ended",
                  },
                ]}
              />
            ) : (
              <p className="text-sm text-[var(--text-secondary)]">
                You do not have this product yet. Ask us from Requests and we will set it up.
              </p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Workspaces" />
          {owned.length === 0 ? (
            <EmptyState
              title="No workspaces"
              description="A workspace is one instance of this product for your organisation."
            />
          ) : (
            <ul className="divide-y divide-[var(--border-subtle)]">
              {owned.map((workspace) => (
                <li key={workspace.id} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-[var(--text-primary)]">
                        {workspace.name}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2">
                        <StatusBadge status={workspace.status} />
                        {workspace.primaryHost && (
                          <span className="truncate text-xs text-[var(--text-secondary)]">
                            {workspace.primaryHost}
                          </span>
                        )}
                      </div>
                    </div>
                    {workspace.status === "ACTIVE" && (
                      <Button
                        size="sm"
                        variant="soft"
                        icon={<ArrowUpRight className="size-3.5" />}
                        onClick={() => open(workspace.id)}
                      >
                        Open
                      </Button>
                    )}
                  </div>

                  {/* The limits the product enforces, read straight off the workspace so
                      what is shown here and what the product allows cannot disagree. */}
                  {moduleAccessEnabled && <Button variant="outline" className="mt-3 w-full sm:w-auto" onClick={() => setModuleWorkspace(workspace)}>
                    Modules & features
                  </Button>}
                  {Object.keys(workspace.entitlements).some((key) => !isModuleEntitlement(key)) && (
                    <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {Object.entries(workspace.entitlements).filter(([key]) => !isModuleEntitlement(key)).map(([key, value]) => (
                        <div
                          key={key}
                          className="rounded-[var(--radius-control)] bg-[var(--surface-sunken)] px-2.5 py-2"
                        >
                          <dt className="text-2xs uppercase tracking-wider text-[var(--text-muted)]">
                            {key.split(".").slice(1).join(" ") || key}
                          </dt>
                          <dd className="numeric text-sm text-[var(--text-primary)]">
                            {value === "-1" ? "unlimited" : value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      {moduleAccessEnabled && moduleWorkspace && <WorkspaceModulesSheet workspace={moduleWorkspace} onClose={() => setModuleWorkspace(null)} />}
    </>
  );
}
